import { useEffect, useState } from 'react'
import { atom, type PrimitiveAtom, useAtom } from 'jotai'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { useNavigate } from "react-router-dom"
import DoNotDisturbAltRoundedIcon from '@mui/icons-material/DoNotDisturbAltRounded'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import GiteRoundedIcon from '@mui/icons-material/GiteRounded'
import Chip from '@mui/material/Chip'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import DoneRoundedIcon from '@mui/icons-material/DoneRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'

import LoadingBox from "@/components/loading-box"
import ErrorBox from "@/components/error-box"
import {
    MMParticipation,
    PayerType,
    type Pagination,
} from '@/types'
import PaginationControls from '@/components/pagination-controls'
import { CATEGORIES_COLORS_MAP, HISTORY_RETURN_PARAM_KEY, PAYER_TYPE_LABEL_MAP, ROUTE_SEGMENTS } from '@/constants/general'
import { convertFromCents, printPrice, roundPrice } from '@/helpers'
import { useYearlyFeePayers } from '@/hooks/api/use-yearly-fee-payers'
import CollectionPointFilter from '@/components/filters/collection-point-filter'
import SearchField from '@/components/filters/search-field'
import YearlyFeePayerActions from './actions'
import YearlyFeeDiscountFilter from '@/components/filters/yearly-fee-discount-filter'
import YearlyFeeCollectionMassActions from '../mass-actions'
import YearlyFeePayerForm from '@/components/forms/yearly-fee-payer'

const PAYER_TYPE_ICON_MAP = {
    [PayerType.per_person]: <PersonRoundedIcon color='inherit' />,
    [PayerType.per_household]: <GiteRoundedIcon color='inherit' />,
}

const nameLikeAtom = atom<string>('')
const nameLikeValueAtom = atom<string>('')
const variableSymbolLikeAtom = atom<string>('')
const variableSymbolLikeValueAtom = atom<string>('')
const selectedHouseholdIdAtom = atom<number | null>(null) as PrimitiveAtom<number | null>
const selectedDiscountIdAtom = atom<number | null>(null) as PrimitiveAtom<number | null>
const allPaymentsPaidAtom = atom<MMParticipation>(MMParticipation.all)

const paginationAtom = atom<Pagination>({
    limit: 20,
    offset: 0,
})

const getSlevaLabel = (count: number) =>
    count === 1 ? 'sleva' : count <= 4 ? 'slevy' : 'slev'

function YearlyFeePayersList({
    yearlyFeeCollectionId,
    yearlyFeeCents,
} : {
    yearlyFeeCollectionId: number,
    yearlyFeeCents: number,
}) {

    const navigate = useNavigate()

    const handleClick = (_event: React.MouseEvent<unknown>, id: number) => {
        navigate(`${ROUTE_SEGMENTS.yearlyFeePayers}/${id}?${HISTORY_RETURN_PARAM_KEY}=true`)
    }

    const [create, setCreate] = useState<boolean>(false)

    // Pagination
    const [pagination, setPagination] = useAtom(paginationAtom)

    // Filters
    const [nameLike, setNameLike] = useAtom(nameLikeAtom)
    const [nameLikeValue, setNameLikeValue] = useAtom(nameLikeValueAtom)
    const [variableSymbolLike, setVariableSymbolLike] = useAtom(variableSymbolLikeAtom)
    const [variableSymbolLikeValue, setVariableSymbolLikeValue] = useAtom(variableSymbolLikeValueAtom)
    const [selectedHouseholdId, setSelectedHouseholdId] = useAtom(selectedHouseholdIdAtom)
    const [selectedDiscountId, setSelectedDiscountId] = useAtom(selectedDiscountIdAtom)
    const [allPaymentsPaid, setAllPaymentsPaid] = useAtom(allPaymentsPaidAtom)

    const handleResetFilters = () => {
        setNameLike('')
        setNameLikeValue('')
        setVariableSymbolLike('')
        setVariableSymbolLikeValue('')
        setSelectedHouseholdId(null)
        setSelectedDiscountId(null)
        setAllPaymentsPaid(MMParticipation.all)
    }

    const getFiltersCount = (): number => {
        let count = 0
        if (nameLike !== '') count++
        if (variableSymbolLike !== '') count++
        if (selectedHouseholdId) count++
        if (selectedDiscountId) count++
        if (allPaymentsPaid !== MMParticipation.all) count++
        return count
    }

    const filtersCount = getFiltersCount()

    const handleResetPage = () => {
         if (pagination.offset !== 0) {
            setPagination(prev => ({
                ...prev,
                offset: 0,
            }))
        }
    }
    
    const { status, data, error } = useYearlyFeePayers(
        yearlyFeeCollectionId,
        {
            pagination,
            filters: {
                householdId: selectedHouseholdId,
                discountId: selectedDiscountId,
                nameLike,
                variableSymbolLike,
                allPaymentsPaid,
            },
        },
    )

    if (status === 'pending') {
        return (
            <LoadingBox />
        )
    }

    if (error) {
        return <ErrorBox
            error={error}
            message={`Nepovedlo se načíst seznam poplatníků k výběru poplatku s ID ${yearlyFeeCollectionId}`}
        />
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflowX: 'auto'
        }}>
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                px: 2.5,
                gap: 1.5,
                pb: 2,
                pt: 1,
                alignItems: 'center',
            }}>
                <SearchField
                    setValue={setVariableSymbolLike}
                    inputValue={variableSymbolLikeValue}
                    setInputValue={setVariableSymbolLikeValue}
                    label="Hledat dle var. symbolu"
                    handleResetPage={handleResetPage}
                />
                <SearchField
                    setValue={setNameLike}
                    inputValue={nameLikeValue}
                    setInputValue={setNameLikeValue}
                    label="Hledat dle jména"
                    handleResetPage={handleResetPage}
                />
                <CollectionPointFilter
                    selectedId={selectedHouseholdId}
                    setSelectedId={setSelectedHouseholdId}
                    handleResetPage={handleResetPage}
                />
                <YearlyFeeDiscountFilter
                    selectedId={selectedDiscountId}
                    setSelectedId={setSelectedDiscountId}
                    handleResetPage={handleResetPage}
                />
                <Box sx={{ minWidth: 200 }}>
                    <FormControl fullWidth>
                        <InputLabel id="allPaymentsPaid-select-label" size="small">Stav úhrady</InputLabel>
                        <Select
                            labelId="allPaymentsPaid-select-label"
                            id="allPaymentsPaid-select"
                            value={allPaymentsPaid}
                            name="allPaymentsPaid"
                            label="Stav úhrady"
                            onChange={(e) => {
                                handleResetPage()
                                setAllPaymentsPaid(e.target.value as MMParticipation)
                            }}
                            size="small"
                        >
                            <MenuItem value={MMParticipation.all}>Vše</MenuItem>
                            <MenuItem value={MMParticipation.yes}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: 'state.resolved',
                                            width: 22,
                                            height: 22,
                                            borderRadius: 10,
                                            color: 'white',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            fontSize: '1.2rem',
                                            flexShrink: 0
                                        }}
                                    >
                                        <DoneRoundedIcon fontSize="inherit" />
                                    </Box>
                                    Uhrazeno
                                </Box>
                            </MenuItem>
                            <MenuItem value={MMParticipation.no}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: 'state.reported',
                                            width: 22,
                                            height: 22,
                                            borderRadius: 10,
                                            color: 'white',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            fontSize: '1.2rem',
                                            flexShrink: 0
                                        }}
                                    >
                                        <CloseRoundedIcon fontSize="inherit" />
                                    </Box>
                                    Neuhrazeno
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                {!!filtersCount &&
                    <Tooltip title="Resetovat filtry">
                        <Button
                            onClick={() => handleResetFilters()}
                            startIcon={<DoNotDisturbAltRoundedIcon />}
                            sx={{
                                backgroundColor: 'customPrimaryLight.dark',
                                px: 1,
                            }}
                        >
                            {filtersCount}
                        </Button>
                    </Tooltip>
                }
            </Box>
            {data.records_count === 0 ?
                <Box px={2.5}>
                    {filtersCount ? 'Žádný poplatník neodpovídá filtrům.' : 'Obec nemá žádné poplatníky - začněte jejich vygenerováním ze svozových míst'}
                </Box>
            :
                <Box>
                    <Table
                        size="small"
                        aria-label="Seznam poplatníků"
                        sx={{
                            mt: 1,
                            minWidth: 600,
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ paddingLeft: 3 }}>Variabilní symbol</TableCell>
                                <TableCell>Jméno</TableCell>
                                <TableCell>Typ</TableCell>
                                <TableCell>Svoz. místo</TableCell>
                                <TableCell>Sleva</TableCell>
                                <TableCell>Kategorie</TableCell>
                                <TableCell>Poplatek po slevě</TableCell>
                                <TableCell>Platby</TableCell>
                                <TableCell>K úhradě</TableCell>
                                <TableCell align="right"/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.fee_payer.map(row => {
                                const totalPayments = row.yearly_fee_payments?.length
                                const paidPayments = row.yearly_fee_payments?.filter(payment => payment.paid_at).length
                                return (
                                    <TableRow
                                        key={row.id}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:last-child td, &:last-child th': { border: 0 },
                                        }}
                                        onClick={(event) => handleClick(event, row.id)}
                                        hover
                                    >
                                        <TableCell component="th" scope="row" sx={{ paddingLeft: 3 }}>
                                            {row.variable_symbol}
                                        </TableCell>
                                        <TableCell>
                                            {row.name}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={PAYER_TYPE_LABEL_MAP[row.payer_type]}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    gap: 0.5,
                                                    alignItems: 'center',
                                                    color: 'customGrey.secondaryText',
                                                }}>
                                                    {PAYER_TYPE_ICON_MAP[row.payer_type]}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {/* <Box color="customGrey.secondaryText">
                                                {row.household?.address?.street ?? '-'} {row.household?.address?.street_number ?? '-'}
                                            </Box> */}
                                            <Chip
                                                label={`${row.household?.address?.street ?? '-'} ${row.household?.address?.street_number ?? '-'}`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'customPrimaryLight.main'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box color="customGrey.secondaryText">
                                                {printPrice(roundPrice(convertFromCents(yearlyFeeCents-row.requested_amount_cents)))}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{
                                                display: 'flex',
                                                gap: 0.3,
                                            }}>
                                                {row.mixed_weight_citizen_category?.level &&
                                                    <Tooltip title={`Sleva za hmotnost ${printPrice(roundPrice(convertFromCents(
                                                        row.mixed_weight_citizen_category?.discount_amount_cents
                                                    )))}`}>
                                                        <Box sx={{
                                                            backgroundColor: CATEGORIES_COLORS_MAP[row.mixed_weight_citizen_category?.level],
                                                            width: 22,
                                                            height: 22,
                                                            borderRadius: 10,
                                                            color: 'white',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                        }}>
                                                            {row.mixed_weight_citizen_category?.level}
                                                        </Box>
                                                    </Tooltip>
                                                }
                                                {row.mixed_volume_citizen_category?.level &&
                                                    <Tooltip title={`Sleva za objem ${printPrice(roundPrice(convertFromCents(
                                                        row.mixed_volume_citizen_category?.discount_amount_cents
                                                    )))}`}>
                                                        <Box sx={{
                                                            backgroundColor: CATEGORIES_COLORS_MAP[row.mixed_volume_citizen_category?.level],
                                                            width: 22,
                                                            height: 22,
                                                            borderRadius: 10,
                                                            color: 'white',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                        }}>
                                                            {row.mixed_volume_citizen_category?.level}
                                                        </Box>
                                                    </Tooltip>
                                                }
                                                {row.bio_weight_citizen_category?.level &&
                                                    <Tooltip title={`Sleva za bio ${printPrice(roundPrice(convertFromCents(
                                                        row.bio_weight_citizen_category?.discount_amount_cents
                                                    )))}`}>
                                                        <Box sx={{
                                                            backgroundColor: CATEGORIES_COLORS_MAP[row.bio_weight_citizen_category?.level],
                                                            width: 22,
                                                            height: 22,
                                                            borderRadius: 10,
                                                            color: 'white',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                        }}>
                                                            {row.bio_weight_citizen_category?.level}
                                                        </Box>
                                                    </Tooltip>
                                                }
                                                {row.discounts.length > 0 && (
                                                    <Chip
                                                        label={`+${row.discounts.length} ${getSlevaLabel(row.discounts.length)}`}
                                                        size="small"
                                                        sx={{ backgroundColor: 'discountGreen.main' }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box color="customGrey.secondaryText">
                                                {printPrice(roundPrice(convertFromCents(row.requested_amount_cents)))}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{
                                                display: 'flex',
                                                gap: 1,
                                            }}>
                                                <Box>
                                                    {`${paidPayments}/${totalPayments}`}
                                                </Box>
                                                <Box sx={{
                                                    backgroundColor: paidPayments === totalPayments ? 'state.resolved' : 'state.reported',
                                                    width: 22,
                                                    height: 22,
                                                    borderRadius: 10,
                                                    color: 'white',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    fontSize: '1.2rem'
                                                }}>
                                                    {paidPayments === totalPayments ?
                                                        <DoneRoundedIcon fontSize='inherit'/> :
                                                        <CloseRoundedIcon fontSize='inherit'/>
                                                    }
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {printPrice(roundPrice(convertFromCents(row.unpaid_amount_cents)))}
                                        </TableCell>
                                        <TableCell align="right">
                                            <YearlyFeePayerActions
                                                yearlyFeePayer={row}
                                                allowDetail
                                            />
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                    <PaginationControls
                        pagination={pagination}
                        setPagination={setPagination}
                        currentArrayLength={data.fee_payer.length}
                        recordsCount={data.records_count}
                    />
                    <Box px={2.5} py={1.5} sx={{
                        mt: {
                            xs: 0,
                            lg: create ? 0 : -7,
                        }
                    }}>
                        {create ?
                             <Box maxWidth="sm">
                                <YearlyFeePayerForm
                                    yearlyFeeCollectionId={yearlyFeeCollectionId}
                                    closeCallback={() => setCreate(false)}
                                />
                            </Box>
                        :
                           <Button
                                variant="contained"
                                color="terciary"
                                startIcon={<AddRoundedIcon />}
                                onClick={() => setCreate(true)}
                            >
                                Přidat poplatníka manuálně
                            </Button>
                        }
                    </Box>
                </Box>
            }
            {filtersCount === 0 &&
                <YearlyFeeCollectionMassActions yearlyFeeCollectionId={yearlyFeeCollectionId} />
            }
        </Box>
    )
}

export default YearlyFeePayersList
